import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import type { FeatureStatus } from '../../types';
import type { AdminFeature } from '../../types/admin';
import { adminFeatureApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { FEATURE_STATUS_META, FEATURE_STATUS_OPTIONS } from './statusMeta';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  /** Giai đoạn đích khi tạo mới. */
  phaseId: string;
  /** Bản ghi cần sửa (bắt buộc khi mode='edit'). */
  feature?: AdminFeature | null;
  /** Giá trị order_index gợi ý khi tạo mới (thường = số tính năng hiện có). */
  defaultOrderIndex?: number;
  onClose: () => void;
  /** Gọi sau khi tạo/cập nhật thành công để component cha reload. */
  onSaved: () => void | Promise<void>;
}

/**
 * Dialog thêm/sửa một tính năng. Tách từ FeatureManagement để dùng chung cho
 * cả khu quản trị và màn nghiệm thu (/projects/:id). Tự quản lý state form,
 * khởi tạo lại mỗi khi mở.
 */
export function FeatureFormDialog({
  open,
  mode,
  phaseId,
  feature,
  defaultOrderIndex = 0,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<FeatureStatus>('DRAFT');
  const [orderIndex, setOrderIndex] = useState('0');
  const [saving, setSaving] = useState(false);

  // Khởi tạo lại form mỗi khi mở dialog (theo mode/feature).
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && feature) {
      setName(feature.name);
      setDescription(feature.description ?? '');
      setStatus(feature.status);
      setOrderIndex(String(feature.order_index));
    } else {
      setName('');
      setDescription('');
      setStatus('DRAFT');
      setOrderIndex(String(defaultOrderIndex));
    }
    setSaving(false);
  }, [open, mode, feature, defaultOrderIndex]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      notify('Vui lòng nhập tên tính năng', 'warning');
      return;
    }
    const order = Number(orderIndex);
    if (Number.isNaN(order)) {
      notify('Thứ tự phải là số', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        order_index: order,
        status,
      };
      if (mode === 'create') {
        await adminFeatureApi.create(phaseId, payload);
        notify('Đã tạo tính năng mới', 'success');
      } else if (feature) {
        await adminFeatureApi.update(feature.id, payload);
        notify('Đã cập nhật tính năng', 'success');
      }
      await onSaved();
      onClose();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu tính năng'), 'error');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Thêm tính năng' : 'Sửa tính năng'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tên tính năng"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
          />
          <TextField
            label="Mô tả"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={saving}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Thứ tự"
              type="number"
              value={orderIndex}
              onChange={(e) => setOrderIndex(e.target.value)}
              disabled={saving}
              sx={{ width: 140 }}
            />
            <FormControl fullWidth disabled={saving}>
              <InputLabel id="feature-form-status-label">Trạng thái</InputLabel>
              <Select
                labelId="feature-form-status-label"
                label="Trạng thái"
                value={status}
                onChange={(e) => setStatus(e.target.value as FeatureStatus)}
              >
                {FEATURE_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {FEATURE_STATUS_META[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Hủy
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
